/**
 * Sign-in inputs: a six-box one-time-code field, a PIN pad with dots, and a
 * QR code drawn as SVG in the current text colour (no raster, no raw colour).
 */
import qrcode from 'qrcode-generator';
import { Delete } from 'lucide-react';
import { useEffect, useMemo, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

/* ------------------------------------------------------------------------ */
/* One-time code                                                             */
/* ------------------------------------------------------------------------ */

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once all six digits are present. */
  onComplete?: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
}

const DIGITS = 6;

export function OtpInput({
  value,
  onChange,
  onComplete,
  invalid = false,
  disabled = false,
  autoFocus = false,
  label = '6-digit code',
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: DIGITS }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus) refs.current[Math.min(value.length, DIGITS - 1)]?.focus();
    // Focus only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  const commit = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, DIGITS);
    onChange(clean);
    if (clean.length === DIGITS) onComplete?.(clean);
    return clean;
  };

  const setAt = (i: number, ch: string) => {
    const arr = digits.slice();
    arr[i] = ch;
    const joined = arr.join('');
    const clean = commit(joined);
    const nextIndex = Math.min(clean.length, DIGITS - 1);
    refs.current[ch ? Math.min(i + 1, nextIndex) : i]?.focus();
  };

  const onKey = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) setAt(i, '');
      else if (i > 0) {
        const arr = digits.slice();
        arr[i - 1] = '';
        commit(arr.join(''));
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < DIGITS - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!/\d/.test(text)) return;
    e.preventDefault();
    const clean = commit(text);
    refs.current[Math.min(clean.length, DIGITS - 1)]?.focus();
  };

  return (
    <div
      className="idn-otp"
      role="group"
      aria-label={label}
      data-invalid={invalid ? '' : undefined}
    >
      {digits.map((d, i) => (
        <span key={i} className="idn-otp__cell">
          {i === 3 ? <span className="idn-otp__gap" aria-hidden /> : null}
          <input
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={d}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={DIGITS}
            disabled={disabled}
            aria-label={`Digit ${i + 1}`}
            aria-invalid={invalid || undefined}
            data-filled={d ? '' : undefined}
            onPaste={onPaste}
            onKeyDown={onKey(i)}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              if (v.length > 1) {
                // Autofill or a fast typist: spread the digits.
                const clean = commit(digits.slice(0, i).join('') + v);
                refs.current[Math.min(clean.length, DIGITS - 1)]?.focus();
              } else {
                setAt(i, v);
              }
            }}
          />
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* PIN pad                                                                   */
/* ------------------------------------------------------------------------ */

export interface PinPadProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  /** PINs are 4 to 8 digits; the dots show this many. */
  length?: number;
  invalid?: boolean;
  disabled?: boolean;
}

export function PinPad({
  value,
  onChange,
  onSubmit,
  length = 4,
  invalid = false,
  disabled = false,
}: PinPadProps) {
  const press = (d: string) => {
    if (disabled || value.length >= 8) return;
    const next = value + d;
    onChange(next);
    if (next.length === length) onSubmit(next);
  };
  const back = () => onChange(value.slice(0, -1));

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        press(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        back();
      } else if (e.key === 'Enter' && value.length >= 4) {
        e.preventDefault();
        onSubmit(value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const dots = Math.max(length, value.length);
  return (
    <div className="idn-pin">
      <div
        className="idn-pin__dots"
        role="status"
        aria-label={`${value.length} of ${length} digits entered`}
        data-invalid={invalid ? '' : undefined}
      >
        {Array.from({ length: dots }, (_, i) => (
          <span key={i} data-on={i < value.length ? '' : undefined} />
        ))}
      </div>
      <div className="idn-pin__pad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" onClick={() => press(d)} disabled={disabled}>
            {d}
          </button>
        ))}
        <button
          type="button"
          className="idn-pin__ghost"
          onClick={() => onChange('')}
          disabled={disabled || value.length === 0}
        >
          Clear
        </button>
        <button type="button" onClick={() => press('0')} disabled={disabled}>
          0
        </button>
        <button
          type="button"
          className="idn-pin__ghost"
          onClick={back}
          disabled={disabled || value.length === 0}
          aria-label="Delete last digit"
        >
          <Delete size={18} aria-hidden />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* QR code                                                                   */
/* ------------------------------------------------------------------------ */

/** Dark modules of a QR code for `text`, as a single SVG path (1 unit per module). */
export function qrPath(text: string): { size: number; d: string } {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    }
  }
  return { size: n, d };
}

export function QrCode({
  text,
  label,
  size = 184,
}: {
  text: string;
  label: string;
  size?: number;
}) {
  const { size: n, d } = useMemo(() => qrPath(text), [text]);
  const quiet = 4;
  return (
    <div className="idn-qr">
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox={`${-quiet} ${-quiet} ${n + quiet * 2} ${n + quiet * 2}`}
        shapeRendering="crispEdges"
      >
        <rect
          className="idn-qr__bg"
          x={-quiet}
          y={-quiet}
          width={n + quiet * 2}
          height={n + quiet * 2}
        />
        <path className="idn-qr__fg" d={d} />
      </svg>
    </div>
  );
}
